import { Mark, mergeAttributes } from '@tiptap/core';

import {
  buildEntityHref,
  entityLinkClassName,
  parseEntityHref,
  ENTITY_ID_ATTR,
  ENTITY_LABEL_ATTR,
  ENTITY_TYPE_ATTR,
  type EntityLinkType,
} from '../../services/entityLinks';

/**
 * An in-app link to a Watchkeeper entity, inside the rich-text body of a landing
 * page or an article.
 *
 * A MARK rather than a node, so a curator writes the sentence they want and
 * links whichever words read naturally — "the Submariner Date" can point at the
 * reference without the prose being interrupted by a chip that behaves like an
 * atom. The trade-off is that the link text is editable and can drift from the
 * entity's real name, which is why the canonical name is stored on the mark as
 * well; see `entityLabel` below.
 *
 * Serialisation is a plain `<a>` carrying the deep link plus the two data
 * attributes the RN WebView renderer matches on, so the same HTML string is
 * valid content for any renderer that has never heard of entity links — it just
 * shows the text.
 *
 * Parse priority is 1001 on purpose. `@tiptap/extension-link` sits at 1000 and
 * its rule (`a[href]`) also matches our anchors; ProseMirror's DOM parser takes
 * the first matching rule in priority order, so ours has to be strictly above
 * Link's or a saved body would come back as an ordinary web link and lose the
 * entity attributes on the next save.
 */

export interface EntityLinkAttributes {
  entityType: EntityLinkType | null;
  entityId: string | null;
  entityLabel: string | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    entityLink: {
      /** Mark the current selection as a link to `type`/`id`. */
      setEntityLink: (attrs: {
        entityType: EntityLinkType;
        entityId: string;
        entityLabel?: string;
      }) => ReturnType;
      /** Strip the entity link from the current selection. */
      unsetEntityLink: () => ReturnType;
    };
  }
}

export const EntityLinkMark = Mark.create({
  name: 'entityLink',

  priority: 1001,

  /**
   * Matches `@tiptap/extension-link`: typing at the very end of a link should
   * produce plain text rather than silently extending the link, so a curator
   * cannot grow "Rolex" into "Rolex makes" and ship a sentence fragment that all
   * taps through to the brand.
   */
  inclusive: false,

  addAttributes() {
    return {
      entityType: {
        default: null,
        parseHTML: (element) => element.getAttribute(ENTITY_TYPE_ATTR),
        renderHTML: (attributes) =>
          attributes.entityType
            ? { [ENTITY_TYPE_ATTR]: attributes.entityType }
            : {},
      },
      entityId: {
        default: null,
        parseHTML: (element) => element.getAttribute(ENTITY_ID_ATTR),
        renderHTML: (attributes) =>
          attributes.entityId ? { [ENTITY_ID_ATTR]: attributes.entityId } : {},
      },
      /**
       * The entity's canonical name when the link was inserted. The link TEXT is
       * prose the curator owns and may reword; this is the name the app needs.
       * `ExploreCalibreDetail` in particular takes a REQUIRED `calibreName`
       * param, so a calibre link that carried only an id would arrive at the
       * screen with nothing to render in the header.
       */
      entityLabel: {
        default: null,
        parseHTML: (element) => element.getAttribute(ENTITY_LABEL_ATTR),
        renderHTML: (attributes) =>
          attributes.entityLabel
            ? { [ENTITY_LABEL_ATTR]: attributes.entityLabel }
            : {},
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `a[${ENTITY_TYPE_ATTR}]`,
        /**
         * The attributes are the source of truth, not the href — an anchor whose
         * type or id is missing or unrecognised is not something this build can
         * route, so it is handed back to the Link extension as an ordinary link
         * rather than being imported as a half-formed entity mark.
         */
        getAttrs: (element) => {
          if (typeof element === 'string') return false;
          const href = (element as HTMLElement).getAttribute('href') ?? '';
          return parseEntityHref(href) ? null : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const entityType = HTMLAttributes[ENTITY_TYPE_ATTR] as
      | EntityLinkType
      | undefined;
    const entityId = HTMLAttributes[ENTITY_ID_ATTR] as string | undefined;

    /**
     * Defensive rather than expected: `setEntityLink` only ever runs with a
     * picker result. A mark somehow missing its ids still has to render as
     * something, and inert text beats an anchor pointing at a broken deep link.
     */
    if (!entityType || !entityId) {
      return ['span', mergeAttributes(HTMLAttributes), 0];
    }

    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        href: buildEntityHref(entityType, entityId),
        class: entityLinkClassName(entityType),
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setEntityLink:
        ({ entityType, entityId, entityLabel }) =>
        ({ chain }) =>
          chain()
            .setMark(this.name, {
              entityType,
              entityId,
              entityLabel: entityLabel ?? null,
            })
            .run(),

      unsetEntityLink:
        () =>
        ({ chain }) =>
          chain().extendMarkRange(this.name).unsetMark(this.name).run(),
    };
  },
});
